import React, { useState, useEffect, useMemo } from "react";
import { Badge, Button, Spinner } from "reactstrap";
import { useForm } from "react-hook-form";
import CommonTableIndexWise, { Column } from "Components/Common/CommonTableIndexWise";
import CommonForm from "Components/Common/CommonForm";
import { useSubjectDetailsService } from "modules/Clinical/SubjectDetails/api/useSubjectDetailsHook";
import FullPageLoader from "Components/Common/FullPageLoader";
import { formatDate, showToastMessage, GetUserData } from "common/service/CommonService";
import { Period, AddDeviationReasonPayload } from "modules/Clinical/TimePoints/helpers/timpoints.types";
import { useGlobalStudy } from "context/StudyContext";
import styles from "modules/Clinical/SubjectDetails/css/subjectinfoperiodsTab.module.scss";

// Extracted Sub-Components
import ManualEntrySummaryCards from "./ManualEntrySummaryCards";
import AddEditDeviationReasonModal from "./AddEditDeviationReasonModal";
import SingleRowReasonModal from "./SingleRowReasonModal";
import SingleRowVerifyModal from "./SingleRowVerifyModal";

// Helper & Type Imports
import {
  PeriodRow,
  DeviationReasonTarget,
  RowReasonTarget,
} from "../helper/manualEntry.types";
import {
  isDeviationStatus,
  deviationBadge,
  calculateDeviation,
  isVerified,
  buildTableRows,
  getFieldName,
} from "../helper/manualEntryConstant";

/* ================================================================
   MANUAL ENTRY SUBJECT PERIOD INFO COMPONENT
================================================================ */

const ManualEntrySubjectPeriodInfo: React.FC = () => {
  const { selectedStudy: globalStudy } = useGlobalStudy();
  const studyid = globalStudy?.studyid
    ? Number(globalStudy.studyid)
    : Number(localStorage.getItem("activestudyid"));
  const subjectno = localStorage.getItem("subjectno") || "";

  // Accordion & Selection State
  const [activePeriodId, setActivePeriodId] = useState<number | null>(null);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Single Row & Bulk Editing State
  const [rowEditingPeriodId, setRowEditingPeriodId] = useState<number | null>(null);
  const [rowEditingTimepoint, setRowEditingTimepoint] = useState<string | null>(null);
  const [editingPeriodId, setEditingPeriodId] = useState<number | null>(null);

  // Modal 1: Edit Reason Target State
  const [isRowReasonModalOpen, setIsRowReasonModalOpen] = useState(false);
  const [rowReasonTarget, setRowReasonTarget] = useState<RowReasonTarget | null>(null);

  // Modal 2: Verification Modal Target State
  const [rowVerifyData, setRowVerifyData] = useState<any>(null);
  const [rowVerifyModalPeriod, setRowVerifyModalPeriod] = useState<Period | null>(null);
  const [loadingVerifyId, setLoadingVerifyId] = useState<number | null>(null);

  // Modal 3: Deviation Reason Target State
  const [isDeviationReasonModalOpen, setIsDeviationReasonModalOpen] = useState(false);
  const [deviationReasonTarget, setDeviationReasonTarget] = useState<DeviationReasonTarget | null>(null);

  // React Hook Form for Single Row Time Selection
  const singleRowForm = useForm<{ samplecollectiontime: string }>({
    defaultValues: { samplecollectiontime: "" },
  });

  // React Hook Form for Bulk Time Selection
  const bulkForm = useForm<Record<string, string>>({
    defaultValues: {},
  });

  // API Service Hook
  const {
    SelectAllQuery,
    UpdateActualTimesMutation,
    CorrectSingleTimeMutation,
    AddDeviationReasonMutation,
    SelectVerifyMutation,
    VerifyCorrectionMutation,
  } = useSubjectDetailsService({
    studyid,
    subjectno,
  });

  const { data: queryResponse, isLoading, isFetching } = SelectAllQuery;
  const periods: Period[] = queryResponse?.data?.periods || [];
  const summary = queryResponse?.data?.summary;

  // Initialize first active period
  useEffect(() => {
    if (periods.length > 0 && !hasInitialized) {
      const firstPeriodKey = Number(periods[0].period_sort);
      setActivePeriodId(firstPeriodKey);
      setHasInitialized(true);
    }
  }, [periods, hasInitialized]);

  const togglePeriod = (id: number) => {
    setActivePeriodId((prev) => (prev === id ? null : id));
  };

  // Dynamically compute timepoint deviation
  const overlayPeriods = useMemo(() => {
    return periods.map((period) => ({
      ...period,
      timepoints: period.timepoints.map((tp) => {
        const actualTime = tp.samplecollectiontime || "";
        const computedDeviation =
          tp.deviation && tp.deviation !== "Pending"
            ? tp.deviation
            : calculateDeviation(tp.actualdosetime, actualTime);

        return {
          ...tp,
          samplecollectiontime: actualTime,
          samplecollectionstatus: tp.samplecollectionstatus || "Pending",
          deviation: computedDeviation,
        };
      }),
    }));
  }, [periods]);

  const globalDeviationCount = useMemo(() => {
    return overlayPeriods.reduce((total, period) => {
      const countInPeriod = period.timepoints.filter((tp) =>
        isDeviationStatus(tp.deviationstatus)
      ).length;
      return total + countInPeriod;
    }, 0);
  }, [overlayPeriods]);

  const getFieldName = (tpNum: string) => `tp_${String(tpNum).replace(/\./g, "_")}`;

  const startEditingPeriod = (period: Period) => {
    const pId = period.periodid || period.period_sort || 0;
    setActivePeriodId(pId);
    setEditingPeriodId(pId);

    const initialTimes: Record<string, string> = {};
    period.timepoints.forEach((tp) => {
      const isCollected =
        !!(tp.samplecollectiontime && tp.samplecollectiontime !== "--") ||
        tp.samplecollectionstatus?.toLowerCase() === "collected";
      if (!isCollected) {
        let timeStr = tp.samplecollectiontime || "";
        if (timeStr && timeStr !== "--") {
          const parts = timeStr.split(":");
          timeStr = parts.slice(0, 2).join(":");
        }
        const key = getFieldName(tp.timepoint_number || "");
        initialTimes[key] = timeStr;
      }
    });
    bulkForm.reset(initialTimes);
  };

  const cancelEditing = () => {
    setEditingPeriodId(null);
    bulkForm.reset({});
  };

  const saveBulkPeriodTimes = (period: Period) => {
    const bulkValues = bulkForm.getValues();
    const records: any[] = [];
    period.timepoints.forEach((tp) => {
      const isCollected =
        !!(tp.samplecollectiontime && tp.samplecollectiontime !== "--") ||
        tp.samplecollectionstatus?.toLowerCase() === "collected";
      if (!isCollected) {
        const key = getFieldName(tp.timepoint_number || "");
        const timeVal = bulkValues[key] || "";
        if (timeVal && timeVal !== "--") {
          const formattedTime = timeVal.split(":").length === 2 ? `${timeVal}:00` : timeVal;
          records.push({
            samplecollectionid: tp.samplecollectionid || 0,
            timepoint_number: tp.timepoint_number || "",
            samplecollectiontime: formattedTime,
          });
        }
      }
    });

    if (records.length === 0) {
      showToastMessage("error", "No collection times entered.");
      return;
    }

    const formData = new FormData();
    formData.append("studyid", String(studyid));
    formData.append("subjectno", subjectno);
    formData.append("period_number", period.period_number || "");
    formData.append("updatedby", String(GetUserData()?.userid || 1));
    formData.append("updatereason", "Manual Entry");
    formData.append("records", JSON.stringify(records));

    UpdateActualTimesMutation.mutate(formData, {
      onSuccess: (res: any) => {
        showToastMessage("success", res?.message || "Sample collection times updated successfully.");
        setEditingPeriodId(null);
        bulkForm.reset({});
      },
      onError: (err: any) => {
        showToastMessage("error", err?.message || "Failed to update collection times.");
      },
    });
  };

  /* ----------------------------------------------------------------
     FEATURE 1: ADD SAMPLE COLLECTION TIME
  ---------------------------------------------------------------- */
  const saveSingleRowTimeAdd = (period: Period, row: any, newValue: string) => {
    const samplecollectionid = row.samplecollectionid;
    const timepoint_number = row.timepoint_number || row.timepoint || "";
    const samplecollectiontime = newValue
      ? newValue.split(":").length === 2 ? `${newValue}:00` : newValue
      : null;

    const records = [
      {
        samplecollectionid,
        timepoint_number,
        samplecollectiontime,
      },
    ];

    const formData = new FormData();
    formData.append("studyid", String(studyid));
    formData.append("subjectno", subjectno);
    formData.append("period_number", period.period_number || "");
    formData.append("updatedby", String(GetUserData()?.userid));
    formData.append("updatereason", "Manual Entry");
    formData.append("records", JSON.stringify(records));

    UpdateActualTimesMutation.mutate(formData, {
      onSuccess: (res: any) => {
        showToastMessage("success", res?.message || "Sample collection time added successfully.");
        setRowEditingPeriodId(null);
        setRowEditingTimepoint(null);
        singleRowForm.reset({ samplecollectiontime: "" });
      },
      onError: (err: any) => {
        showToastMessage("error", err?.message || "Failed to add collection time.");
      },
    });
  };

  /* ----------------------------------------------------------------
     FEATURE 2: EDIT SAMPLE COLLECTION TIME + REASON SUBMISSION
  ---------------------------------------------------------------- */
  const saveSingleRowTimeEdit = (reasonText: string) => {
    if (!rowReasonTarget) return;
    const { row, newValue } = rowReasonTarget;
    const samplecollectionid = row.samplecollectionid;
    if (!samplecollectionid) {
      showToastMessage("error", "Sample collection ID not found for this timepoint.");
      return;
    }

    const samplecollectiontime = newValue
      ? newValue.split(":").length === 2 ? `${newValue}:00` : newValue
      : "";

    const payload = {
      samplecollectionid: Number(samplecollectionid),
      samplecollectiontime,
      updatedby: Number(GetUserData()?.userid),
      updatereason: reasonText,
    };

    CorrectSingleTimeMutation.mutate(payload, {
      onSuccess: (res: any) => {
        showToastMessage("success", res?.message || "Sample collection time saved successfully.");
        setRowEditingPeriodId(null);
        setRowEditingTimepoint(null);
        singleRowForm.reset({ samplecollectiontime: "" });
        setIsRowReasonModalOpen(false);
        setRowReasonTarget(null);
      },
      onError: (err: any) => {
        showToastMessage("error", err?.message || "Failed to save collection time.");
      },
    });
  };

  /* ----------------------------------------------------------------
     FEATURE 3: VERIFY CORRECTION MODAL HANDLERS
  ---------------------------------------------------------------- */
  const handleRowVerifyClick = (samplecollectionid: number, period: Period) => {
    if (!samplecollectionid) return;
    setLoadingVerifyId(samplecollectionid);

    SelectVerifyMutation.mutate({ samplecollectionid }, {
      onSuccess: (res: any) => {
        setLoadingVerifyId(null);
        if (res.statusCode === 200 && res.data) {
          const detail = Array.isArray(res.data) ? res.data[0] : res.data;
          setRowVerifyData(detail);
          setRowVerifyModalPeriod(period);
        } else {
          showToastMessage("error", res.message || "Failed to fetch verification details.");
        }
      },
      onError: (err: any) => {
        setLoadingVerifyId(null);
        showToastMessage("error", err?.message || "Failed to fetch verification details.");
      },
    });
  };

  const handleRowPinVerifySubmit = (pin: string) => {
    if (!rowVerifyData) return;

    const payload = {
      samplecollectionid: Number(rowVerifyData.samplecollectionid),
      verifiedby: Number(GetUserData()?.userid),
      verifypin: pin,
    };

    VerifyCorrectionMutation.mutate(payload, {
      onSuccess: (res: any) => {
        showToastMessage("success", res?.message || "Correction verified successfully.");
        setRowVerifyData(null);
        setRowVerifyModalPeriod(null);
      },
      onError: (err: any) => {
        showToastMessage("error", err?.message || err || "Failed to verify sample collection time.");
      },
    });
  };

  /* ----------------------------------------------------------------
     FEATURE 4: ADD / EDIT DEVIATION REASON MODAL HANDLERS
  ---------------------------------------------------------------- */
  const handleOpenDeviationReasonModal = (period: Period, row: any) => {
    const existingReason = row.deviationreason || "";
    setDeviationReasonTarget({ period, row, currentReason: existingReason });
    setIsDeviationReasonModalOpen(true);
  };

  const handleSaveDeviationReason = (reasonText: string) => {
    if (!deviationReasonTarget || !reasonText.trim()) return;
    const { row } = deviationReasonTarget;
    if (!row.samplecollectionid) {
      showToastMessage("error", "Sample collection ID not found for this timepoint.");
      return;
    }

    const payload: AddDeviationReasonPayload = {
      studyid: Number(studyid),
      samplecollectionid: Number(row.samplecollectionid),
      updatedby: Number(GetUserData()?.userid),
      deviationreason: reasonText.trim(),
    };

    AddDeviationReasonMutation.mutate(payload, {
      onSuccess: (res: any) => {
        showToastMessage("success", res?.message || "Deviation reason saved successfully.");
        setIsDeviationReasonModalOpen(false);
        setDeviationReasonTarget(null);
      },
      onError: (err: any) => {
        showToastMessage("error", err?.message || "Failed to save deviation reason.");
      },
    });
  };

  /* ----------------------------------------------------------------
     TABLE COLUMNS DEFINITION
  ---------------------------------------------------------------- */
  const getColumnsForPeriod = (period: Period): Column[] => {
    const pId = period.periodid || period.period_sort || 0;

    return [
      { key: "srno", label: "Sr.No", textCenter: true },
      { key: "timepoint", label: "Time Point", textCenter: true },
      { key: "scheduledTime", label: "Dose Time", textCenter: true },
      {
        key: "dosingscheduledate",
        label: "Scheduled Date",
        textCenter: true,
        render: (val: string) => <span>{formatDate(val) || "--"}</span>,
      },
      {
        key: "actualTime",
        label: "Actual Schedule Time",
        textCenter: true,
        render: (val: string) => <span>{val || "--"}</span>,
      },
      {
        key: "samplecollectiontime",
        label: "Sample Collection Time",
        textCenter: true,
        render: (val: string, row: any) => {
          const isBulkEditing = editingPeriodId === pId;
          const isRowCollected =
            !!(row.samplecollectiontime && row.samplecollectiontime !== "--") ||
            row.samplecollectionstatus?.toLowerCase() === "collected";

          if (isBulkEditing) {
            if (isRowCollected) {
              return <span className="text-secondary">{val || "--"}</span>;
            }

            const key = getFieldName(row.timepoint || "");
            return (
              <div style={{ width: "160px", margin: "0 auto" }}>
                <CommonForm
                  fields={[
                    {
                      name: key,
                      type: "time",
                      placeholder: "--:--",
                    },
                  ]}
                  control={bulkForm.control}
                  columns={1}
                  labelShow={false}
                  parentClass="m-0 p-0"
                />
              </div>
            );
          }

          const isRowEditing = rowEditingPeriodId === pId && rowEditingTimepoint === row.timepoint;

          if (isRowEditing) {
            return (
              <div
                className="d-flex align-items-center justify-content-center gap-1"
                style={{ width: "235px", margin: "0 auto" }}
              >
                <div style={{ width: "140px" }}>
                  <CommonForm
                    fields={[
                      {
                        name: "samplecollectiontime",
                        type: "time",
                        placeholder: "--:--",
                      },
                    ]}
                    control={singleRowForm.control}
                    columns={1}
                    labelShow={false}
                    parentClass="m-0 p-0"
                  />
                </div>
                <button
                  type="button"
                  className="btn btn-light btn-sm border d-flex align-items-center justify-content-center text-success"
                  style={{ width: "28px", height: "28px", borderRadius: "6px", cursor: "pointer" }}
                  onClick={() => {
                    let initialTime = "";
                    if (val && val !== "--") {
                      const parts = val.split(":");
                      initialTime = parts.slice(0, 2).join(":");
                    }
                    const rowVal = singleRowForm.getValues("samplecollectiontime") || "";
                    if (rowVal === initialTime) {
                      setRowEditingPeriodId(null);
                      setRowEditingTimepoint(null);
                      singleRowForm.reset({ samplecollectiontime: "" });
                      return;
                    }

                    if (!val || val === "--") {
                      saveSingleRowTimeAdd(period, row, rowVal);
                    } else {
                      setRowReasonTarget({
                        period,
                        row,
                        prevValue: val || "--",
                        newValue: rowVal,
                      });
                      setIsRowReasonModalOpen(true);
                    }
                  }}
                  title="Save"
                >
                  <i className="ri-check-line fs-15 fw-bold"></i>
                </button>
                <button
                  type="button"
                  className="btn btn-light btn-sm border d-flex align-items-center justify-content-center text-danger"
                  style={{ width: "28px", height: "28px", borderRadius: "6px", cursor: "pointer" }}
                  onClick={() => {
                    setRowEditingPeriodId(null);
                    setRowEditingTimepoint(null);
                    singleRowForm.reset({ samplecollectiontime: "" });
                  }}
                  title="Cancel"
                >
                  <i className="ri-close-line fs-15 fw-bold"></i>
                </button>
              </div>
            );
          }

          const isPeriodVerified =
            period.timepoints.length > 0 && period.timepoints.every((tp) => isVerified(tp.isverified));
          const canRowEdit = !isPeriodVerified && !editingPeriodId;

          return (
            <div className="d-flex align-items-center justify-content-center gap-2">
              <span className="text-secondary">{val || "--"}</span>
              {row.iscorrection &&
                (row.iscorrectionverified ? (
                  <span
                    className="badge bg-success-subtle text-success border border-success-subtle rounded-pill px-1.5 py-0.5 fs-10 d-inline-flex align-items-center gap-0.5"
                    title="Correction Verified"
                  >
                    <i className="ri-checkbox-circle-fill fs-12"></i>
                    Verified
                  </span>
                ) : (
                  <button
                    type="button"
                    className="btn btn-warning btn-xs py-0.5 px-2 rounded-pill fs-10 d-inline-flex align-items-center gap-1 text-white border-0"
                    style={{ backgroundColor: "#eab308" }}
                    disabled={loadingVerifyId === row.samplecollectionid}
                    onClick={() => handleRowVerifyClick(row.samplecollectionid, period)}
                    title="Verify Correction"
                  >
                    {loadingVerifyId === row.samplecollectionid ? (
                      <Spinner size="sm" style={{ width: "12px", height: "12px", margin: "0" }} />
                    ) : (
                      <i className="ri-shield-keyhole-line fs-11"></i>
                    )}
                    Verify
                  </button>
                ))}
              {canRowEdit && (
                <button
                  type="button"
                  className="btn btn-link p-0 d-flex align-items-center justify-content-center border-0 bg-transparent"
                  onClick={() => {
                    setRowEditingPeriodId(pId);
                    setRowEditingTimepoint(row.timepoint);
                    let initialTime = "";
                    if (val && val !== "--") {
                      const parts = val.split(":");
                      initialTime = parts.slice(0, 2).join(":");
                    }
                    singleRowForm.reset({ samplecollectiontime: initialTime });
                  }}
                  style={{ textDecoration: "none", cursor: "pointer" }}
                  title={isRowCollected ? "Edit collection time" : "Add collection time"}
                >
                  {isRowCollected ? (
                    <i className="ri-edit-line fs-14 text-primary"></i>
                  ) : (
                    <i className="ri-add-line fs-16 text-success fw-bold"></i>
                  )}
                </button>
              )}
            </div>
          );
        },
      },
      {
        key: "dosingstation",
        label: "Dosing Station No",
        textCenter: true,
        render: (val: string) => <span>{val || "--"}</span>,
      },
      {
        key: "deviation",
        label: "Deviation",
        textCenter: true,
        render: (val: string, row: any) => {
          const isRowEditing = rowEditingPeriodId === pId && rowEditingTimepoint === row.timepoint;
          const key = getFieldName(row.timepoint || "");
          const actualTime =
            editingPeriodId === pId
              ? bulkForm.watch(key) || row.samplecollectiontime || ""
              : isRowEditing
              ? singleRowForm.watch("samplecollectiontime")
              : row.samplecollectiontime || "";
          const devStr = actualTime ? calculateDeviation(row.actualTime, actualTime) : "Pending";
          return deviationBadge(devStr);
        },
      },
      {
        key: "deviationreason",
        label: "Deviation Reason",
        textCenter: true,
        render: (val: string, row: any) => {
          const isEligible = isDeviationStatus(row.deviationstatus);
          if (!isEligible) {
            return <span className="text-secondary">{row.deviationreason || val || "--"}</span>;
          }

          const existingReason = row.deviationreason || val;
          const hasReason = !!existingReason && existingReason !== "--";

          if (hasReason) {
            return (
              <div className="d-flex align-items-center justify-content-center gap-1">
                <span className="text-dark fw-medium fs-12">{existingReason}</span>
                <button
                  type="button"
                  className="btn btn-link p-0 text-primary border-0 bg-transparent ms-1"
                  onClick={() => handleOpenDeviationReasonModal(period, row)}
                  title="Edit Deviation Reason"
                  style={{ cursor: "pointer", textDecoration: "none" }}
                >
                  <i className="ri-edit-line fs-14"></i>
                </button>
              </div>
            );
          }

          return (
            <button
              type="button"
              className="btn btn-warning btn-xs py-0.5 px-2 rounded-pill fs-11 fw-semibold d-inline-flex align-items-center gap-1 text-white border-0 shadow-sm"
              style={{ backgroundColor: "#eab308", cursor: "pointer" }}
              onClick={() => handleOpenDeviationReasonModal(period, row)}
              title="Add Deviation Reason"
            >
              <i className="ri-add-line fs-12 fw-bold"></i>
              Add Reason
            </button>
          );
        },
      },
      {
        key: "deviationstatus",
        label: "Deviation Status",
        textCenter: true,
        render: (val: string, row: any) => (
          <span className="text-secondary">{row.deviationstatus || val || "--"}</span>
        ),
      },
    ];
  };

  if (isLoading || isFetching) {
    return <FullPageLoader />;
  }

  return (
    <div>
      {/* SUMMARY METRIC CARDS */}
      {summary && (
        <ManualEntrySummaryCards
          totalPeriods={summary.total_periods || 0}
          totalTimePoints={summary.total_timepoints || 0}
          deviationsFound={globalDeviationCount}
        />
      )}

      {/* PERIOD ACCORDION LIST */}
      {overlayPeriods.map((period) => {
        const pId = period.periodid || period.period_sort || 0;
        const isOpen = activePeriodId === pId;
        const columns = getColumnsForPeriod(period);
        const rows = buildTableRows(period);
        const periodDeviationCount = period.timepoints.filter((tp) =>
          isDeviationStatus(tp.deviationstatus)
        ).length;

        return (
          <div key={pId} className={styles.periodSection}>
            {/* Period Header */}
            <div
              className={styles.periodHeader}
              onClick={() => togglePeriod(pId)}
              style={{
                borderBottom: isOpen ? "1px solid rgba(30, 107, 145, 0.1)" : "none",
                cursor: "pointer",
              }}
            >
              <div className={styles.headerLeft}>
                <i className={`ri-arrow-${isOpen ? "down" : "right"}-s-line ${styles.arrowIcon}`} />
                <h6 className={styles.periodTitle}>
                  {period.period_name || period.periodname || `Period ${period.period_number}`}
                </h6>
              </div>
              <div className={styles.headerRight} onClick={(e) => e.stopPropagation()}>
                {(period.scheduled_date || period.scheduleddate) && (
                  <span className={styles.scheduledDate}>
                    Scheduled Date: {formatDate(period.scheduled_date || period.scheduleddate)}
                  </span>
                )}
                <Badge className={styles.countBadge}>
                  {period.timepoints.length} Time Points
                </Badge>
                <Badge
                  className={periodDeviationCount > 0 ? styles.deviationBadge : styles.countBadge}
                >
                  {periodDeviationCount} {periodDeviationCount === 1 ? "Deviation" : "Deviations"}
                </Badge>

                {editingPeriodId === pId ? (
                  <div className="d-inline-flex gap-2 ms-2">
                    <Button
                      color="success"
                      size="sm"
                      className="d-inline-flex align-items-center gap-1 py-1 px-3 fs-12 fw-semibold"
                      disabled={UpdateActualTimesMutation.isPending}
                      onClick={() => saveBulkPeriodTimes(period)}
                    >
                      {UpdateActualTimesMutation.isPending ? (
                        <>
                          <Spinner size="sm" style={{ width: "12px", height: "12px" }} />
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="ri-save-line fs-14"></i>
                          Save All
                        </>
                      )}
                    </Button>
                    <Button
                      color="light"
                      size="sm"
                      className="d-inline-flex align-items-center gap-1 py-1 px-2 fs-12 border"
                      onClick={cancelEditing}
                    >
                      <i className="ri-close-line fs-14"></i>
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <Button
                    color="primary"
                    outline
                    size="sm"
                    className="ms-2 d-inline-flex align-items-center gap-1 py-1 px-2.5 fs-12 fw-semibold"
                    onClick={() => startEditingPeriod(period)}
                  >
                    <i className="ri-edit-box-line fs-14"></i>
                    Bulk Enter Times
                  </Button>
                )}
              </div>
            </div>

            {/* Period Table Container */}
            {isOpen && (
              <div className={styles.tableContainer}>
                <div className="pt-3">
                  <CommonTableIndexWise columns={columns} data={rows} paginationShow={false} />
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* FEATURE 2: REASON MODAL FOR EDITING SINGLE TIME */}
      <SingleRowReasonModal
        isOpen={isRowReasonModalOpen}
        toggle={() => {
          setIsRowReasonModalOpen(false);
          setRowReasonTarget(null);
        }}
        target={rowReasonTarget}
        onSubmit={saveSingleRowTimeEdit}
        isPending={CorrectSingleTimeMutation.isPending}
      />

      {/* FEATURE 3: VERIFICATION DETAILS & SECURITY PIN MODAL */}
      <SingleRowVerifyModal
        isOpen={!!rowVerifyData}
        toggle={() => {
          setRowVerifyData(null);
          setRowVerifyModalPeriod(null);
        }}
        verifyData={rowVerifyData}
        modalPeriod={rowVerifyModalPeriod}
        onSubmitPin={handleRowPinVerifySubmit}
        isPending={VerifyCorrectionMutation.isPending}
      />

      {/* FEATURE 4: ADD / EDIT DEVIATION REASON MODAL */}
      <AddEditDeviationReasonModal
        isOpen={isDeviationReasonModalOpen}
        toggle={() => {
          setIsDeviationReasonModalOpen(false);
          setDeviationReasonTarget(null);
        }}
        target={deviationReasonTarget}
        onSave={handleSaveDeviationReason}
        isPending={AddDeviationReasonMutation.isPending}
      />
    </div>
  );
};

export default ManualEntrySubjectPeriodInfo;
